import Region from "../Region";
import RegionArea from "../RegionArea";
import Phaser from 'phaser';

import * as ABCya from '@abcya/abcya_phaser';
import UIAssetKeys from '../UIAssetKeys';
import { AREAS, GameData, REGIONS, SHAPES } from '../gameData';
import SoundAssetKeys from "../SoundAssetKeys";

export default class SandcastleArea extends RegionArea
{
    /**
     * Create the sandcastle area.
     * @param {Region} region the owner region
     */
    constructor(parentRegion)
    {
        super(parentRegion, AREAS[REGIONS.BEACH].SANDCASTLE);

        this._createArea();

        this.reset();
    }

    /**
     * Reset should be called when you want to set the state to a disabled/inactive state.
     */
    reset()
    {
        super.reset();

        this.leftCylinder.setAlpha(0);
        this.rightCylinder.setAlpha(0);
        this.leftCone.setAlpha(0);
        this.rightCone.setAlpha(0);
    }

    /**
     * Prepare should be called before you want to prepare the state to become active.
     */
    prepare()
    {
        super.prepare();

        this.leftCylinder.disableInteractive();
        this.rightCylinder.disableInteractive();
        this.leftCone.disableInteractive();
        this.rightCone.disableInteractive();
    }

    /**
     * Send all game objects to their start positions.
     */
    _sendObjectsToStartPositions()
    {
        super._sendObjectsToStartPositions();

        this.leftCylinder.setAlpha(0.000001);
        this.rightCylinder.setAlpha(0.000001);
        this.leftCone.setAlpha(0.000001);
        this.rightCone.setAlpha(0.000001);
    }

    /**
     * Creates the area, by adding the overlays to the parent region and creates the area information sidebar.
     */
    _createArea()
    {
        let leftCylinderHitArea = new Phaser.Geom.Polygon(
            [
                { x: 59, y: 253 },
                { x: 0, y: 210 },
                { x: 0, y: 41 },
                { x: 59, y: 0 },
                { x: 117, y: 41 },
                { x: 117, y: 210 },
            ]);
        this.leftCylinder = ABCya.Images.addImage(550, 454, UIAssetKeys._05_Beach_Zoom_In_Sand_Castle_SndCstl_Slct_Clndr1, this.parentRegion).setInteractive(leftCylinderHitArea, Phaser.Geom.Polygon.Contains);
        this.leftCylinder.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, () => 
        {
            this._onOverlayPointerDown(this.leftCylinder, SHAPES.CYLINDER);
        });

        let rightCylinderHitArea = new Phaser.Geom.Polygon(
            [
                { x: 59, y: 336 },
                { x: 0, y: 295 },
                { x: 0, y: 42 },
                { x: 59, y: 0 },
                { x: 117, y: 42 },
                { x: 117, y: 295 },
            ]);
        this.rightCylinder = ABCya.Images.addImage(823, 404, UIAssetKeys._05_Beach_Zoom_In_Sand_Castle_SndCstl_Slct_Clndr2, this.parentRegion).setInteractive(rightCylinderHitArea, Phaser.Geom.Polygon.Contains);
        this.rightCylinder.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, () => 
        {
            this._onOverlayPointerDown(this.rightCylinder, SHAPES.CYLINDER);
        });

        let coneHitArea = new Phaser.Geom.Polygon(
            [
                { x: 69, y: 0 },
                { x: 137, y: 155 },
                { x: 69, y: 194 },
                { x: 0, y: 155 },
            ]);
        this.leftCone = ABCya.Images.addImage(551, 322, UIAssetKeys._05_Beach_Zoom_In_Sand_Castle_SndCstl_Slct_Cone, this.parentRegion).setInteractive(coneHitArea, Phaser.Geom.Polygon.Contains);
        this.leftCone.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, () =>
        {
            this._onOverlayPointerDown(this.leftCone, SHAPES.CONE);
        });
        this.rightCone = ABCya.Images.addImage(823, 229, UIAssetKeys._05_Beach_Zoom_In_Sand_Castle_SndCstl_Slct_Cone, this.parentRegion).setInteractive(coneHitArea, Phaser.Geom.Polygon.Contains);
        this.rightCone.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, () =>
        {
            this._onOverlayPointerDown(this.rightCone, SHAPES.CONE);
        });

        this.shapeOverlays[SHAPES.CYLINDER] = [this.leftCylinder, this.rightCylinder];
        this.shapeOverlays[SHAPES.CONE] = [this.leftCone, this.rightCone];

        this._createAreaInformation(0x5f5fd6, 0x5f5fd6);
    }

    /**
     * Callback function when an overlay is clicked on. Extends the original function and hides the area instructions.
     * @param {Phaser.GameObjects.Image} overlay the shape overlay that was clicked on
     * @param {string} shape the SHAPE enum value that the overlay belongs to
     */
    _onOverlayPointerDown(overlay, shape)
    {
        if (this.parentRegion.areaInstructionsPopup.state == 1)
        {
            this.parentRegion.areaInstructionsPopup.state = 2;
            ABCya.Tweens.addTween(this.parentRegion.areaInstructionsPopup, 350, { y: this.parentRegion.areaInstructionsPopup.offScreenPosition.y }, Phaser.Math.Easing.Back.In);
        }

        super._onOverlayPointerDown(overlay, shape);
    }

    /**
     * Creates the shape information for the area. These are the buttons that can be clicked to find the shapes.
     * Extends the original function and adds an additional listener to show the 2nd set of instructions when a shape name is clicked.
     * @param {number} numberFontColor the number font color
     * @param {number} textFontColor the text font color
     */
    _createAreaInformation(numberFontColor, textFontColor)
    {
        super._createAreaInformation(numberFontColor, textFontColor);

        for (let shape in GameData.region_data[this.region][this.area])
        {
            let containerShapeObj = this.areaInformation[shape];
            containerShapeObj.shapeButton.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, () => 
            {
                if (this.parentRegion.areaInstructionsPopup.state == 0)
                {
                    this.parentRegion.areaInstructionsPopup.state = 1;
                    if (this.parentRegion.areaInstructionsPopup.firstInstructionsAudio.isPlaying)
                    {
                        this.parentRegion.areaInstructionsPopup.firstInstructionsAudio.stop();
                    }
                    ABCya.Audio.playOneShot(SoundAssetKeys._locate_that_shape, this.parentRegion.scene, 1, false, 0.25);
                    ABCya.Tweens.addTween(this.parentRegion.areaInstructionsPopup.firstInstructions, 350, { alpha: 0 }, Phaser.Math.Easing.Quadratic.In);
                    ABCya.Tweens.addTween(this.parentRegion.areaInstructionsPopup.secondInstructions, 350, { alpha: 1, delay: 300 }, Phaser.Math.Easing.Quadratic.Out);
                }
            });
        }
    }

    /**
     * Zoom into the area.
     */
    zoomIn()
    {
        let tweens = super.zoomIn();

        tweens.push(
            {
                targets: [
                    this.parentRegion.beachball, this.parentRegion.lifeguardContainer, this.parentRegion.seagullContainer
                ],
                duration: 500,
                props: { alpha: 0 },
                ease: Phaser.Math.Easing.Quadratic.Out,
            },
            { targets: this.parentRegion.sandcastle, duration: 1000, props: { scale: 1, x: 689, y: 390 }, ease: Phaser.Math.Easing.Quadratic.Out, offset: 0 },
            { targets: this.areaInformation, duration: 500, props: { x: this.areaInformation.onScreenPosition.x }, ease: Phaser.Math.Easing.Back.Out, offset: 500 }
        );

        return tweens;
    }

    /**
     * Zoom out of the area.
     */
    zoomOut()
    {
        let tweens = super.zoomOut();

        this.leftCylinder.disableInteractive();
        this.rightCylinder.disableInteractive();
        this.leftCone.disableInteractive();
        this.rightCone.disableInteractive();

        for (let shape in this.shapeOverlays)
        {
            // fade off the overlay
            tweens.push(
                {
                    targets: this.shapeOverlays[shape],
                    duration: 500,
                    props: { alpha: 0 },
                    ease: Phaser.Math.Easing.Quadratic.Out,
                    offset: 0
                }
            );
        }

        tweens.push(
            { targets: this.areaInformation, duration: 500, props: { x: this.areaInformation.offScreenPosition.x }, ease: Phaser.Math.Easing.Back.In, offset: 0 },
            { targets: this.parentRegion.sandcastle, duration: 1000, props: { scale: 0.19, x: this.parentRegion.sandcastle.onScreenPosition.x, y: this.parentRegion.sandcastle.onScreenPosition.y }, ease: Phaser.Math.Easing.Quadratic.Out, offset: 250 },
            { targets: [this.parentRegion.lifeguardContainer, this.parentRegion.beachball, this.parentRegion.seagullContainer], duration: 500, props: { alpha: 1 }, ease: Phaser.Math.Easing.Quadratic.Out, offset: 500 },
        );

        return tweens;
    }
}